/**
 * Haversine distance calculation utilities.
 */

// Earth's radius in meters
const EARTH_RADIUS_METERS = 6_371_000;

/**
 * Convert degrees to radians.
 */
export function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate the great-circle distance between two points using the Haversine formula.
 *
 * @param lat1 - Latitude of point 1 in degrees
 * @param lon1 - Longitude of point 1 in degrees
 * @param lat2 - Latitude of point 2 in degrees
 * @param lon2 - Longitude of point 2 in degrees
 * @returns Distance in meters
 */
export function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const radLat1 = toRadians(lat1);
  const radLat2 = toRadians(lat2);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(radLat1) * Math.cos(radLat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
}

/**
 * Calculate bearing from point 1 to point 2.
 *
 * @param lat1 - Latitude of point 1 in degrees
 * @param lon1 - Longitude of point 1 in degrees
 * @param lat2 - Latitude of point 2 in degrees
 * @param lon2 - Longitude of point 2 in degrees
 * @returns Bearing in degrees (0-360)
 */
export function calculateBearing(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const radLat1 = toRadians(lat1);
  const radLat2 = toRadians(lat2);
  const dLon = toRadians(lon2 - lon1);

  const y = Math.sin(dLon) * Math.cos(radLat2);
  const x =
    Math.cos(radLat1) * Math.sin(radLat2) -
    Math.sin(radLat1) * Math.cos(radLat2) * Math.cos(dLon);

  const bearing = Math.atan2(y, x);
  return ((bearing * 180) / Math.PI + 360) % 360;
}

/**
 * Calculate the destination point given a start point, bearing, and distance.
 *
 * @param lat - Start latitude in degrees
 * @param lon - Start longitude in degrees
 * @param bearing - Bearing in degrees
 * @param distance - Distance in meters
 * @returns [latitude, longitude] of destination point
 */
export function destinationPoint(
  lat: number,
  lon: number,
  bearing: number,
  distance: number
): [number, number] {
  const radLat = toRadians(lat);
  const radLon = toRadians(lon);
  const radBearing = toRadians(bearing);
  const angularDistance = distance / EARTH_RADIUS_METERS;

  const destLat = Math.asin(
    Math.sin(radLat) * Math.cos(angularDistance) +
      Math.cos(radLat) * Math.sin(angularDistance) * Math.cos(radBearing)
  );

  const destLon =
    radLon +
    Math.atan2(
      Math.sin(radBearing) * Math.sin(angularDistance) * Math.cos(radLat),
      Math.cos(angularDistance) - Math.sin(radLat) * Math.sin(destLat)
    );

  return [(destLat * 180) / Math.PI, (destLon * 180) / Math.PI];
}
